import { useMemo } from 'react';

import { getServiceId } from '@/api/tokens.helper';
import { CATALOGUE_RIGHTS, MARINA_RIGHTS } from '@/permissions/route-rights';
import { usePermissions } from '@/permissions/usePermissions';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store';
import { useBerthsQuery } from '@/store/vessel-marinas/vessel-marinas.store';

import { sortBerths } from './berth.utils';

/**
 * My Marinas: the vessel's berths, and the catalogue marinas it starred. Each list is
 * asked for only with the right that reads it — berths with `MARINA_RIGHTS`, the
 * catalogue with `CATALOGUE_RIGHTS` — so without it the list stays empty, not an error.
 * The active vessel's, or `vesselId`'s when a page shows another of yours.
 */
export const useSavedMarinas = (vesselId = getServiceId()) => {
  const { canAny } = usePermissions(vesselId);
  const canRead = canAny([...MARINA_RIGHTS]);
  const canBrowse = canAny([...CATALOGUE_RIGHTS]);

  const berthsQuery = useBerthsQuery({ enabled: canRead, vesselId });
  // ponytail: first 100 starred marinas; nobody stars more yet.
  const starredQuery = useServiceProvidersQuery(
    100,
    { vesselId, type: 'marina', isFavorite: true, search: '' },
    { enabled: canBrowse },
  );

  const berths = useMemo(() => sortBerths(berthsQuery.data ?? []), [berthsQuery.data]);
  const starred = useMemo(
    () => starredQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [starredQuery.data],
  );

  return { canRead, canBrowse, berthsQuery, starredQuery, berths, starred };
};
